import { useState } from "react";
import { forgotPassword } from "../services/api";
import { Link } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // quick client-side validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setMsg("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      const res = await forgotPassword(email);
      if (res.ok) {
        setSuccess(true);
        setMsg("Check your email for a reset link.");
      } else {
        setMsg(res.error || "Email not found.");
      }
    } catch (err) {
      console.error(err);
      setMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070b14] flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white">SiteScope</h1>
          <p className="mt-2 text-sm text-gray-500">24/7 Website Monitoring</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="sm:px-10">
          <Card.Header>
            <Card.Title className="text-center">Reset your password</Card.Title>
            <Card.Description className="text-center">
              Enter your email address and we'll send you a reset link
            </Card.Description>
          </Card.Header>

          {success ? (
            <Card.Content>
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-500/15 mb-4">
                  <svg className="h-6 w-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Check your email</h3>
                <p className="text-gray-400 mb-6">
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
                <Button as={Link} to="/login" variant="primary" className="w-full">
                  Back to Sign In
                </Button>
              </div>
            </Card.Content>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none block w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 sm:text-sm transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {msg && (
                <div className={`rounded-xl p-4 ${
                  success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
                }`}>
                  <div className={`text-sm ${
                    success ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {msg}
                  </div>
                </div>
              )}

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </div>
            </form>
          )}

          <Card.Footer>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-emerald-400 hover:text-emerald-300"
              >
                Remember your password? Sign in
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
